import * as TabsPrimitive from 'radix-ui/tabs';
import type { ReactNode } from 'react';
import { cx } from '../cx';

/**
 * Tabs. The strip scrolls horizontally instead of wrapping or shrinking, so a
 * record page with six sections still works at 360px.
 */
export interface TabDef {
  readonly value: string;
  readonly label: string;
  /** Small trailing count, e.g. the number of records behind the tab. */
  readonly badge?: number | undefined;
}

export interface TabsProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly tabs: readonly TabDef[];
  readonly children: ReactNode;
  readonly ariaLabel: string;
  readonly className?: string | undefined;
}

export function Tabs({ value, onValueChange, tabs, children, ariaLabel, className }: TabsProps) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange} className={className}>
      <TabsPrimitive.List
        aria-label={ariaLabel}
        className="flex gap-1 overflow-x-auto border-b border-line"
      >
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            className={cx(
              // `touch-manipulation` and no press fill: a tab answers with its underline
              // and its ink, and a grey plate behind an underline tab is a control from a
              // different family. What it does need is the tap delay gone.
              'inline-flex min-h-(--touch-target-min) shrink-0 items-center gap-2 border-b-2 border-transparent px-3',
              'touch-manipulation text-sm font-medium text-ink-muted transition-colors',
              'hover:text-ink data-[state=active]:border-accent data-[state=active]:text-ink',
            )}
          >
            {tab.label}
            {tab.badge !== undefined && (
              <span className="text-xs text-ink-subtle tabular-nums">{tab.badge}</span>
            )}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {children}
    </TabsPrimitive.Root>
  );
}

export interface TabPanelProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  return (
    <TabsPrimitive.Content value={value} className={cx('pt-4', className)}>
      {children}
    </TabsPrimitive.Content>
  );
}
